import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Package,
  FolderTree,
  ShoppingBag,
  Users,
  KeyRound,
  Store,
  UserCheck,
  Wallet,
  Settings as SettingsIcon,
  Plus,
  Trash2,
  Edit,
  X,
  IndianRupee,
  ReceiptText,
  CheckCircle2,
  Clock3,
  XCircle,
  TrendingUp,
  UserRound,
  PackageCheck,
  ArrowUpRight,
  RefreshCw,
} from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";

const NAV = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "products", label: "Products", icon: Package },
  { id: "categories", label: "Categories", icon: FolderTree },
  { id: "orders", label: "Orders", icon: ShoppingBag },
  { id: "customers", label: "Customers", icon: Users },
  { id: "licenses", label: "Licenses", icon: KeyRound },
  { id: "applications", label: "Partner Applications", icon: UserCheck },
  { id: "partners", label: "Partners", icon: Store },
  { id: "payouts", label: "Earnings & Payouts", icon: Wallet },
  { id: "settings", label: "Settings", icon: SettingsIcon },
];

const inp = "w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500/50";
const lines = (value) => Array.isArray(value) ? value.join("\n") : (value || "");
const tolines = (value) =>
  Array.isArray(value)
    ? value.filter(Boolean)
    : String(value || "")
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);

export default function AdminPage() {
  const [view, setView] = useState("dashboard");
  return (
    <div className="min-h-screen bg-[#050506] pt-24">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-5 gap-8">
        <aside className="lg:col-span-1">
          <div className="sticky top-28 space-y-1">
            <p className="text-xs uppercase tracking-wider text-gray-500 px-4 mb-3">Admin</p>
            {NAV.map((n) => (
              <button
                key={n.id}
                onClick={() => setView(n.id)}
                data-testid={`admin-nav-${n.id}`}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  view === n.id ? "bg-purple-600 text-white" : "text-gray-400 hover:bg-white/5 hover:text-white"
                }`}
              >
                <n.icon className="w-4 h-4" /> {n.label}
              </button>
            ))}
          </div>
        </aside>
        <main className="lg:col-span-4">
          {view === "dashboard" && <Dashboard />}
          {view === "products" && <Products />}
          {view === "categories" && <Categories />}
          {view === "orders" && <Orders />}
          {view === "customers" && <SimpleTable endpoint="/admin/customers" cols={["name", "email", "role", "purchase_count"]} title="Customers" />}
          {view === "licenses" && <SimpleTable endpoint="/admin/licenses" cols={["product_name", "license_type", "user_id"]} title="Licenses" />}
          {view === "applications" && <Applications />}
          {view === "partners" && <SimpleTable endpoint="/admin/partners" cols={["store_name", "store_slug", "status", "product_count"]} title="Partners" />}
          {view === "payouts" && <Payouts />}
          {view === "settings" && <SiteSettings />}
        </main>
      </div>
    </div>
  );
}

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadStats = async () => {
    try {
      setRefreshing(true);
      const response = await api.get("/admin/stats");
      setStats(response.data);
    } catch (error) {
      toast.error("Could not load dashboard analytics.");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  if (!stats) return <Spinner />;

  const currency = (value) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(Number(value || 0));

  const number = (value) =>
    new Intl.NumberFormat("en-IN").format(Number(value || 0));

  const statusStyles = {
    PAID: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    COMPLETED: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    PARTIALLY_REFUNDED: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    CREATED: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    PENDING: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    AUTHORIZED: "bg-purple-500/10 text-purple-400 border-purple-500/20",
    FAILED: "bg-red-500/10 text-red-400 border-red-500/20",
    CANCELLED: "bg-gray-500/10 text-gray-400 border-gray-500/20",
    REFUNDED: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    CHARGEBACK: "bg-red-500/10 text-red-400 border-red-500/20",
  };

  const primaryCards = [
    {
      label: "Total Revenue",
      value: currency(stats.revenue),
      helper: "Verified paid orders only",
      icon: IndianRupee,
      iconClass: "bg-purple-500/10 text-purple-400",
    },
    {
      label: "Total Orders",
      value: number(stats.total_orders),
      helper: "All checkout attempts",
      icon: ReceiptText,
      iconClass: "bg-blue-500/10 text-blue-400",
    },
    {
      label: "Paid Orders",
      value: number(stats.paid_orders),
      helper: "Successfully completed",
      icon: CheckCircle2,
      iconClass: "bg-emerald-500/10 text-emerald-400",
    },
    {
      label: "Conversion Rate",
      value: `${Number(stats.conversion_rate || 0).toFixed(2)}%`,
      helper: "Paid orders / total orders",
      icon: TrendingUp,
      iconClass: "bg-fuchsia-500/10 text-fuchsia-400",
    },
  ];

  const paymentCards = [
    {
      label: "Pending",
      value: number(stats.pending_orders),
      icon: Clock3,
      className: "text-amber-400",
    },
    {
      label: "Failed",
      value: number(stats.failed_orders),
      icon: XCircle,
      className: "text-red-400",
    },
    {
      label: "Refunded",
      value: number(stats.refunded_orders),
      icon: ArrowUpRight,
      className: "text-blue-400",
    },
    {
      label: "Average Order Value",
      value: currency(stats.average_order_value),
      icon: Wallet,
      className: "text-purple-400",
    },
  ];

  const platformCards = [
    {
      label: "Customers",
      value: number(stats.customers),
      icon: UserRound,
    },
    {
      label: "Published Products",
      value: number(stats.published_products),
      icon: PackageCheck,
    },
    {
      label: "Partners",
      value: number(stats.partners),
      icon: Store,
    },
    {
      label: "Pending Applications",
      value: number(stats.pending_applications),
      icon: UserCheck,
    },
  ];

  const paidPercentage =
    stats.total_orders > 0
      ? Math.min(
          100,
          (Number(stats.paid_orders || 0) /
            Number(stats.total_orders || 1)) *
            100
        )
      : 0;

  const pendingPercentage =
    stats.total_orders > 0
      ? Math.min(
          100,
          (Number(stats.pending_orders || 0) /
            Number(stats.total_orders || 1)) *
            100
        )
      : 0;

  const failedPercentage =
    stats.total_orders > 0
      ? Math.min(
          100,
          (Number(stats.failed_orders || 0) /
            Number(stats.total_orders || 1)) *
            100
        )
      : 0;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-sm text-purple-400 font-medium mb-1">
            OrvDigital Admin
          </p>

          <h1 className="text-3xl font-bold text-white">
            Business Dashboard
          </h1>

          <p className="text-sm text-gray-500 mt-2">
            Revenue, payment health and platform activity.
          </p>
        </div>

        <button
          type="button"
          onClick={loadStats}
          disabled={refreshing}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-gray-300 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-50"
        >
          <RefreshCw
            className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
          />
          Refresh
        </button>
      </div>

      {/* Main KPI cards */}
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {primaryCards.map((card) => {
          const Icon = card.icon;

          return (
            <motion.div
              key={card.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative overflow-hidden p-5 bg-[#101012] border border-white/10 rounded-2xl hover:border-purple-500/30 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500">
                    {card.label}
                  </p>

                  <p className="text-2xl xl:text-3xl font-bold text-white mt-3">
                    {card.value}
                  </p>

                  <p className="text-xs text-gray-500 mt-2">
                    {card.helper}
                  </p>
                </div>

                <div
                  className={`w-11 h-11 rounded-xl flex items-center justify-center ${card.iconClass}`}
                >
                  <Icon className="w-5 h-5" />
                </div>
              </div>

              <div className="absolute -right-8 -bottom-8 w-24 h-24 rounded-full bg-purple-500/5 blur-xl" />
            </motion.div>
          );
        })}
      </div>

      {/* Payment health and overview */}
      <div className="grid xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 p-6 bg-[#101012] border border-white/10 rounded-2xl">
          <div className="flex items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-lg font-semibold text-white">
                Payment Health
              </h2>

              <p className="text-sm text-gray-500 mt-1">
                Status of all checkout attempts.
              </p>
            </div>

            <span className="px-3 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-xs text-purple-400">
              {number(stats.total_orders)} total
            </span>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {paymentCards.map((card) => {
              const Icon = card.icon;

              return (
                <div
                  key={card.label}
                  className="p-4 rounded-xl bg-white/[0.03] border border-white/5"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-500">{card.label}</p>
                    <Icon className={`w-4 h-4 ${card.className}`} />
                  </div>

                  <p className="text-xl font-bold text-white mt-3">
                    {card.value}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="space-y-5 mt-7">
            <div>
              <div className="flex justify-between text-xs mb-2">
                <span className="text-gray-400">Successful payments</span>
                <span className="text-emerald-400">
                  {paidPercentage.toFixed(1)}%
                </span>
              </div>

              <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 rounded-full"
                  style={{ width: `${paidPercentage}%` }}
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between text-xs mb-2">
                <span className="text-gray-400">Pending payments</span>
                <span className="text-amber-400">
                  {pendingPercentage.toFixed(1)}%
                </span>
              </div>

              <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-500 rounded-full"
                  style={{ width: `${pendingPercentage}%` }}
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between text-xs mb-2">
                <span className="text-gray-400">Failed payments</span>
                <span className="text-red-400">
                  {failedPercentage.toFixed(1)}%
                </span>
              </div>

              <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                <div
                  className="h-full bg-red-500 rounded-full"
                  style={{ width: `${failedPercentage}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 bg-[#101012] border border-white/10 rounded-2xl">
          <h2 className="text-lg font-semibold text-white">
            Platform Overview
          </h2>

          <p className="text-sm text-gray-500 mt-1 mb-5">
            Current marketplace activity.
          </p>

          <div className="space-y-3">
            {platformCards.map((card) => {
              const Icon = card.icon;

              return (
                <div
                  key={card.label}
                  className="flex items-center justify-between p-4 rounded-xl bg-white/[0.03] border border-white/5"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-purple-500/10 text-purple-400 flex items-center justify-center">
                      <Icon className="w-4 h-4" />
                    </div>

                    <span className="text-sm text-gray-300">
                      {card.label}
                    </span>
                  </div>

                  <span className="font-semibold text-white">
                    {card.value}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Recent orders */}
      <div className="bg-[#101012] border border-white/10 rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between p-5 sm:p-6 border-b border-white/10">
          <div>
            <h2 className="text-lg font-semibold text-white">
              Recent Orders
            </h2>

            <p className="text-sm text-gray-500 mt-1">
              Latest checkout and payment activity.
            </p>
          </div>

          <span className="text-xs text-gray-500">
            Last {stats.recent_orders?.length || 0} orders
          </span>
        </div>

        {!stats.recent_orders?.length ? (
          <div className="py-16 text-center">
            <ShoppingBag className="w-9 h-9 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No orders available.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="text-left bg-white/[0.025]">
                  <th className="px-5 py-4 text-xs font-medium text-gray-500">
                    Order
                  </th>
                  <th className="px-5 py-4 text-xs font-medium text-gray-500">
                    Customer
                  </th>
                  <th className="px-5 py-4 text-xs font-medium text-gray-500">
                    Product
                  </th>
                  <th className="px-5 py-4 text-xs font-medium text-gray-500">
                    Amount
                  </th>
                  <th className="px-5 py-4 text-xs font-medium text-gray-500">
                    Status
                  </th>
                  <th className="px-5 py-4 text-xs font-medium text-gray-500">
                    Created
                  </th>
                </tr>
              </thead>

              <tbody>
                {stats.recent_orders.map((order) => {
                  const status = String(
                    order.status || "pending"
                  ).toLowerCase();

                  const productName =
                    order.items?.[0]?.product_name ||
                    order.product_name ||
                    "Digital product";

                  const createdAt = order.created_at
                    ? new Date(order.created_at).toLocaleString("en-IN", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })
                    : "—";

                  return (
                    <tr
                      key={order.id || order.order_number}
                      className="border-t border-white/5 hover:bg-white/[0.025] transition-colors"
                    >
                      <td className="px-5 py-4">
                        <p className="font-medium text-white">
                          {order.order_number || "—"}
                        </p>
                      </td>

                      <td className="px-5 py-4">
                        <p className="text-gray-300">
                          {order.user_name || "Customer"}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          {order.user_email || "—"}
                        </p>
                      </td>

                      <td className="px-5 py-4 text-gray-300">
                        {productName}
                      </td>

                      <td className="px-5 py-4 font-medium text-white">
                        {currency(order.amount)}
                      </td>

                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full border text-xs capitalize ${
                            statusStyles[String(status || "").toUpperCase()] ||
                            "bg-gray-500/10 text-gray-400 border-gray-500/20"
                          }`}
                        >
                          {status}
                        </span>
                      </td>

                      <td className="px-5 py-4 text-xs text-gray-500">
                        {createdAt}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Business insight */}
      <div className="p-5 sm:p-6 rounded-2xl border border-purple-500/20 bg-gradient-to-r from-purple-500/10 via-purple-500/5 to-transparent">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-purple-500/15 text-purple-400 flex items-center justify-center shrink-0">
            <TrendingUp className="w-5 h-5" />
          </div>

          <div>
            <h3 className="text-white font-semibold">
              Business Performance
            </h3>

            <p className="text-sm text-gray-400 mt-1">
              {stats.total_orders > 0
                ? `${Number(
                    stats.conversion_rate || 0
                  ).toFixed(
                    2
                  )}% of checkout attempts became successful purchases. Revenue only includes verified paid orders.`
                : "Your analytics will appear here after customers begin checking out."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
const Spinner = () => (
  <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" /></div>
);

const Orders = () => {
  const [orders, setOrders] = useState(null);
  useEffect(() => { api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => setOrders([])); }, []);
  if (!orders) return <Spinner />;
  const color = (status) => ({
    COMPLETED: "text-emerald-400", PAID: "text-emerald-400", PARTIALLY_REFUNDED: "text-blue-400",
    CREATED: "text-amber-400", PENDING: "text-amber-400", AUTHORIZED: "text-purple-400",
    FAILED: "text-red-400", CANCELLED: "text-gray-400", REFUNDED: "text-blue-400", CHARGEBACK: "text-red-400",
  }[String(status || "").toUpperCase()] || "text-gray-400");
  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6">Orders</h1>
      <div className="space-y-3">
        {orders.map((order) => (
          <details key={order.id} className="p-5 bg-white/5 border border-white/10 rounded-xl">
            <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
              <div><p className="text-white font-medium">{order.order_number}</p><p className="text-xs text-gray-500">{order.user_email}</p></div>
              <div className="text-right"><p className="text-white font-bold">₹{order.amount}</p><p className={`text-xs ${color(order.status)}`}>{String(order.status || "").replaceAll("_", " ")}</p></div>
            </summary>
            <div className="mt-5 border-t border-white/5 pt-4 space-y-3">
              {(order.timeline || []).slice().reverse().map((entry, index) => (
                <div key={`${entry.event_id || entry.timestamp}-${index}`} className="flex gap-3 text-sm">
                  <span className={`mt-1.5 h-2 w-2 rounded-full bg-current ${color(entry.status)}`} />
                  <div><p className="text-gray-300">{String(entry.status || "").replaceAll("_", " ")}</p><p className="text-xs text-gray-500">{entry.detail || entry.event_type || entry.source}</p><p className="text-xs text-gray-600">{entry.timestamp ? new Date(entry.timestamp).toLocaleString() : ""}</p></div>
                </div>
              ))}
            </div>
          </details>
        ))}
      </div>
    </div>
  );
};

const SimpleTable = ({ endpoint, cols, title }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(endpoint).then((r) => setRows(r.data)).catch(() => setRows([])); }, [endpoint]);
  if (!rows) return <Spinner />;
  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6">{title}</h1>
      {rows.length === 0 ? <p className="text-gray-400">No records.</p> : (
        <div className="overflow-x-auto border border-white/10 rounded-2xl">
          <table className="w-full text-sm">
            <thead className="bg-white/5">
              <tr>{cols.map((c) => <th key={c} className="text-left px-4 py-3 text-gray-400 font-medium capitalize">{c.replace(/_/g, " ")}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-white/5">
                  {cols.map((c) => <td key={c} className="px-4 py-3 text-gray-300">{String(r[c] ?? "-")}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const Categories = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", icon: "Layers" });
  const load = () => api.get("/admin/categories").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    try { await api.post("/admin/categories", form); toast.success("Added"); setForm({ name: "", description: "", icon: "Layers" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/categories/${id}`); load(); };
  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6">Categories</h1>
      <div className="flex flex-col sm:flex-row gap-2 mb-6">
        <input placeholder="Name" className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input placeholder="Description" className={inp} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <Button onClick={add} className="bg-purple-600 text-white whitespace-nowrap"><Plus className="w-4 h-4 mr-1" />Add</Button>
      </div>
      <div className="space-y-2">
        {rows.map((c) => (
          <div key={c.id} className="flex justify-between items-center p-4 bg-white/5 border border-white/10 rounded-xl">
            <div><p className="text-white">{c.name}</p><p className="text-xs text-gray-500">{c.slug}</p></div>
            <button onClick={() => del(c.id)} className="text-gray-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const Applications = () => {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/partner-applications").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const review = async (id, action) => {
    try { await api.post(`/admin/partner-applications/${id}/review`, { action }); toast.success(action === "approve" ? "Approved" : "Rejected"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6">Partner Applications</h1>
      {rows.length === 0 ? <p className="text-gray-400">No applications.</p> : (
        <div className="space-y-3">
          {rows.map((a) => (
            <div key={a.id} className="p-5 bg-white/5 border border-white/10 rounded-xl">
              <div className="flex justify-between items-start flex-wrap gap-3">
                <div>
                  <p className="text-white font-medium">{a.store_name} <span className="text-xs text-gray-500">by {a.full_name}</span></p>
                  <p className="text-xs text-gray-500">{a.email} · {a.phone}</p>
                  <p className="text-sm text-gray-400 mt-2">{a.plan}</p>
                </div>
                {a.status === "pending" ? (
                  <div className="flex gap-2">
                    <Button onClick={() => review(a.id, "approve")} className="bg-green-600 hover:bg-green-500 text-white" data-testid={`approve-${a.id}`}>Approve</Button>
                    <Button onClick={() => review(a.id, "reject")} variant="outline" className="border-white/20 text-white bg-transparent">Reject</Button>
                  </div>
                ) : <span className={`text-xs capitalize px-3 py-1 rounded-full ${a.status === "approved" ? "bg-green-500/15 text-green-400" : "bg-red-500/15 text-red-400"}`}>{a.status}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Payouts = () => {
  const [earnings, setEarnings] = useState([]);
  const [partners, setPartners] = useState([]);
  const [form, setForm] = useState({ partner_id: "", amount: "", reference: "", note: "" });
  const load = () => {
    api.get("/admin/earnings").then((r) => setEarnings(r.data)).catch(() => {});
    api.get("/admin/partners").then((r) => setPartners(r.data)).catch(() => {});
  };
  useEffect(load, []);
  const release = async () => { const r = await api.post("/admin/earnings/release"); toast.success(`Released ${r.data.released} earnings`); load(); };
  const pay = async () => {
    try { await api.post("/admin/payouts", { ...form, amount: Number(form.amount) }); toast.success("Payout recorded"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-white">Earnings & Payouts</h1>
        <Button onClick={release} className="bg-purple-600 text-white">Release Due Earnings</Button>
      </div>
      <div className="p-5 bg-white/5 border border-white/10 rounded-2xl mb-8">
        <p className="text-white font-semibold mb-3">Record Manual Payout</p>
        <div className="grid sm:grid-cols-4 gap-2">
          <select className={inp} value={form.partner_id} onChange={(e) => setForm({ ...form, partner_id: e.target.value })}>
            <option value="" className="bg-[#0B0B0D]">Select partner</option>
            {partners.map((p) => <option key={p.id} value={p.id} className="bg-[#0B0B0D]">{p.store_name}</option>)}
          </select>
          <input placeholder="Amount" type="number" className={inp} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          <input placeholder="Reference" className={inp} value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
          <Button onClick={pay} className="bg-purple-600 text-white">Record</Button>
        </div>
      </div>
      <SimpleEarnings earnings={earnings} />
    </div>
  );
};

const SimpleEarnings = ({ earnings }) => (
  <div className="space-y-2">
    {earnings.length === 0 ? <p className="text-gray-400">No earnings recorded.</p> : earnings.map((e) => (
      <div key={e.id} className="flex justify-between p-4 bg-white/5 border border-white/10 rounded-xl text-sm">
        <span className="text-gray-300">{e.product_name}</span>
        <span className="text-white">₹{e.amount} <span className="text-gray-500 capitalize">({e.status})</span></span>
      </div>
    ))}
  </div>
);

const SiteSettings = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setS({
        meta_tracking_enabled: true,
        google_tracking_enabled: true,
        social: {},
        ...r.data,
      });
    }).catch(() => toast.error("Could not load settings"));
  }, []);

  if (!s) return <Spinner />;

  const social = s.social || {};
  const metaValid = !s.meta_pixel_id || /^\d{5,25}$/.test(s.meta_pixel_id.trim());
  const googleValid =
    !s.google_analytics_id ||
    /^(G|GT)-[A-Z0-9]+$/i.test(s.google_analytics_id.trim());
  const validUrl = (value) => !value || /^https?:\/\//i.test(value.trim());
  const youtubeLooksWrong =
    social.youtube &&
    /instagram\.com/i.test(social.youtube);

  const setField = (key, value) => setS((current) => ({ ...current, [key]: value }));
  const setSocial = (key, value) =>
    setS((current) => ({
      ...current,
      social: { ...(current.social || {}), [key]: value },
    }));

  const save = async () => {
    if (!metaValid || !googleValid) {
      toast.error("Please correct the tracking IDs before saving.");
      return;
    }
    if (!["instagram", "facebook", "youtube"].every((key) => validUrl(social[key]))) {
      toast.error("Social links must start with https://");
      return;
    }
    if (youtubeLooksWrong) {
      toast.error("The YouTube field currently contains an Instagram link.");
      return;
    }

    try {
      setSaving(true);
      await api.put("/admin/settings", {
        ...s,
        meta_pixel_id: (s.meta_pixel_id || "").trim(),
        google_analytics_id: (s.google_analytics_id || "").trim().toUpperCase(),
        social: Object.fromEntries(
          Object.entries(social).map(([key, value]) => [key, String(value || "").trim()])
        ),
      });
      toast.success("Settings and tracking configuration saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const TextField = ({ label, value, onChange, placeholder, error, helper }) => (
    <div>
      <label className="text-sm font-medium text-gray-300 mb-2 block">{label}</label>
      <input
        className={`${inp} h-11 ${error ? "border-red-500/60 focus:border-red-500" : ""}`}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {(error || helper) && (
        <p className={`text-xs mt-2 ${error ? "text-red-400" : "text-gray-500"}`}>
          {error || helper}
        </p>
      )}
    </div>
  );

  const Toggle = ({ checked, onChange, label, description }) => (
    <label className="flex items-start justify-between gap-5 p-4 rounded-xl bg-white/[0.03] border border-white/5 cursor-pointer">
      <span>
        <span className="text-sm font-medium text-white block">{label}</span>
        <span className="text-xs text-gray-500 mt-1 block">{description}</span>
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 accent-purple-600"
      />
    </label>
  );

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <p className="text-sm text-purple-400 font-medium mb-1">Website configuration</p>
        <h1 className="text-3xl font-bold text-white">Settings</h1>
        <p className="text-sm text-gray-500 mt-2">
          Manage contact details, analytics, conversion tracking and public social profiles.
        </p>
      </div>

      <section className="p-5 sm:p-6 bg-[#101012] border border-white/10 rounded-2xl">
        <div className="mb-5">
          <h2 className="text-lg font-semibold text-white">Contact & announcement</h2>
          <p className="text-sm text-gray-500 mt-1">Public support information shown across OrvDigital.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <TextField
            label="Contact email"
            value={s.contact_email}
            onChange={(value) => setField("contact_email", value)}
            placeholder="hello@orvdigital.com"
          />
          <TextField
            label="Support email"
            value={s.support_email}
            onChange={(value) => setField("support_email", value)}
            placeholder="support@orvdigital.com"
          />
          <div className="md:col-span-2">
            <TextField
              label="Announcement bar text"
              value={s.announcement}
              onChange={(value) => setField("announcement", value)}
              placeholder="New products available now"
            />
          </div>
        </div>
      </section>

      <section className="p-5 sm:p-6 bg-[#101012] border border-white/10 rounded-2xl">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
          <div>
            <h2 className="text-lg font-semibold text-white">Meta Pixel</h2>
            <p className="text-sm text-gray-500 mt-1">
              Tracks PageView, ViewContent, InitiateCheckout and Purchase.
            </p>
          </div>
          <span className={`self-start px-3 py-1 rounded-full border text-xs ${
            s.meta_pixel_id && metaValid
              ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"
              : "bg-amber-500/10 border-amber-500/20 text-amber-400"
          }`}>
            {s.meta_pixel_id && metaValid ? "Configured" : "Needs Pixel ID"}
          </span>
        </div>
        <div className="space-y-4">
          <Toggle
            checked={s.meta_tracking_enabled !== false}
            onChange={(value) => setField("meta_tracking_enabled", value)}
            label="Enable Meta tracking"
            description="Load the Meta Pixel and send standard ecommerce browser events."
          />
          <TextField
            label="Meta Pixel ID"
            value={s.meta_pixel_id}
            onChange={(value) => setField("meta_pixel_id", value)}
            placeholder="Example: 123456789012345"
            error={!metaValid ? "Pixel ID should contain numbers only." : ""}
            helper="Find this in Meta Events Manager → Data Sources → Pixel."
          />
          <div className="p-4 rounded-xl bg-purple-500/5 border border-purple-500/15">
            <p className="text-xs text-purple-300 leading-relaxed">
              After deployment, verify events with Meta Pixel Helper and Events Manager → Test Events.
              Purchase fires only after your backend verifies the Razorpay payment.
            </p>
          </div>
        </div>
      </section>

      <section className="p-5 sm:p-6 bg-[#101012] border border-white/10 rounded-2xl">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
          <div>
            <h2 className="text-lg font-semibold text-white">Google Analytics 4</h2>
            <p className="text-sm text-gray-500 mt-1">
              Tracks page views and matching ecommerce events.
            </p>
          </div>
          <span className={`self-start px-3 py-1 rounded-full border text-xs ${
            s.google_analytics_id && googleValid
              ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"
              : "bg-amber-500/10 border-amber-500/20 text-amber-400"
          }`}>
            {s.google_analytics_id && googleValid ? "Configured" : "Needs Measurement ID"}
          </span>
        </div>
        <div className="space-y-4">
          <Toggle
            checked={s.google_tracking_enabled !== false}
            onChange={(value) => setField("google_tracking_enabled", value)}
            label="Enable Google Analytics"
            description="Load GA4 and track navigation across the single-page application."
          />
          <TextField
            label="GA4 Measurement ID"
            value={s.google_analytics_id}
            onChange={(value) => setField("google_analytics_id", value)}
            placeholder="G-XXXXXXXXXX"
            error={!googleValid ? "Use a GA4 ID beginning with G- or GT-." : ""}
            helper="Find this in GA4 → Admin → Data Streams → Web."
          />
        </div>
      </section>

      <section className="p-5 sm:p-6 bg-[#101012] border border-white/10 rounded-2xl">
        <div className="mb-5">
          <h2 className="text-lg font-semibold text-white">Social profiles</h2>
          <p className="text-sm text-gray-500 mt-1">
            Valid links automatically appear as Instagram, Facebook and YouTube icons in the footer.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {[
            ["instagram", "Instagram URL", "https://www.instagram.com/orvdigital/"],
            ["facebook", "Facebook URL", "https://www.facebook.com/your-page"],
            ["youtube", "YouTube URL", "https://www.youtube.com/@your-channel"],
          ].map(([key, label, placeholder]) => (
            <TextField
              key={key}
              label={label}
              value={social[key]}
              onChange={(value) => setSocial(key, value)}
              placeholder={placeholder}
              error={
                !validUrl(social[key])
                  ? "URL must start with https://"
                  : key === "youtube" && youtubeLooksWrong
                    ? "This is an Instagram URL. Paste your YouTube channel URL."
                    : ""
              }
            />
          ))}
        </div>
      </section>

      <div className="flex justify-end pb-6">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-purple-600 hover:bg-purple-500 text-white px-6"
        >
          {saving ? "Saving..." : "Save settings"}
        </Button>
      </div>
    </div>
  );
};

// ---------------- Products management ----------------
const Products = () => {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [cats, setCats] = useState([]);
  const load = () => api.get("/admin/products").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); api.get("/admin/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);
  const del = async (id) => { if (window.confirm("Delete this product?")) { await api.delete(`/admin/products/${id}`); load(); } };
  if (editing !== null) return <ProductEditor product={editing} cats={cats} onClose={() => { setEditing(null); load(); }} />;
  if (!rows) return <Spinner />;
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-white">Products</h1>
        <Button onClick={() => setEditing({})} className="bg-purple-600 text-white" data-testid="admin-new-product"><Plus className="w-4 h-4 mr-1" />New Product</Button>
      </div>
      <div className="space-y-2">
        {rows.map((p) => (
          <div key={p.id} className="flex justify-between items-center p-4 bg-white/5 border border-white/10 rounded-xl">
            <div className="flex items-center gap-3">
              {p.cover_image && <img src={p.cover_image} alt="" className="w-12 h-12 rounded object-cover" />}
              <div>
                <p className="text-white">{p.name}</p>
                <p className="text-xs text-gray-500 capitalize">{p.status} · {p.category_name}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing(p)} className="text-gray-400 hover:text-white p-2"><Edit className="w-4 h-4" /></button>
              <button onClick={() => del(p.id)} className="text-gray-400 hover:text-red-400 p-2"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ProductEditor = ({ product, cats, onClose }) => {
  const isNew = !product.id;
  const lic = product.licenses || { creator: { enabled: true, price: 299, description: "", rights: [] }, reseller: { enabled: false, price: 599, description: "", rights: [] } };
  const partner = product.partner || { enabled: false, base_price: 299, min_price: 299, max_price: 599 };
  const delivery = product.delivery || { pdf_url: "", instructions: "" };
  const [f, setF] = useState({
    name: product.name || "", category_id: product.category_id || "", cover_image: product.cover_image || "",
    gallery: lines(product.gallery), sample_url: product.sample_url || "", short_description: product.short_description || "", full_description: product.full_description || "",
    highlights: lines(product.highlights), whats_included: lines(product.whats_included), who_its_for: lines(product.who_its_for),
    how_it_works: lines(product.how_it_works), delivery_info: product.delivery_info || "", status: product.status || "draft",
    featured: product.featured || false, badge: product.badge || "",
    seo_title: product.seo_title || "", seo_description: product.seo_description || "",
    cr_enabled: lic.creator?.enabled ?? true, cr_price: lic.creator?.price ?? 299, cr_desc: lic.creator?.description || "", cr_pdf: lic.creator?.pdf_url || "", cr_rights: lines(lic.creator?.rights),
    rs_enabled: lic.reseller?.enabled ?? false, rs_price: lic.reseller?.price ?? 599, rs_desc: lic.reseller?.description || "", rs_pdf: lic.reseller?.pdf_url || "", rs_rights: lines(lic.reseller?.rights),
    p_enabled: partner.enabled, p_base: partner.base_price, p_min: partner.min_price, p_max: partner.max_price,
    d_pdf: delivery.pdf_url, d_instr: delivery.instructions,
  });

  const [uploadingCover, setUploadingCover] = useState(false);

  const uploadCoverImage = async (file) => {
  if (!file) return;

  setUploadingCover(true);

  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("bucket", "product-images");

    const res = await api.post("/admin/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    set("cover_image", res.data.url);
    toast.success("Cover image uploaded");
  } catch (err) {
    toast.error(formatApiError(err));
  } finally {
    setUploadingCover(false);
  }
};

  const [uploadingGallery, setUploadingGallery] = useState(false);

const uploadGalleryImages = async (files) => {
  if (!files || files.length === 0) return;

  setUploadingGallery(true);

  try {
    const uploadedUrls = [];

    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("bucket", "product-images");

      const res = await api.post("/admin/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      uploadedUrls.push(res.data.url);
    }

    set(
  "gallery",
  [f.gallery, ...uploadedUrls].filter(Boolean).join("\n")
);
    toast.success("Gallery images uploaded");
  } catch (err) {
    toast.error(formatApiError(err));
  } finally {
    setUploadingGallery(false);
  }
};
  const set = (k, v) => setF({ ...f, [k]: v });

  const save = async () => {
    const payload = {
      name: f.name, category_id: f.category_id || null, cover_image: f.cover_image, gallery: tolines(f.gallery), sample_url: f.sample_url,
      short_description: f.short_description, full_description: f.full_description,
      highlights: tolines(f.highlights), whats_included: tolines(f.whats_included), who_its_for: tolines(f.who_its_for),
      how_it_works: tolines(f.how_it_works), delivery_info: f.delivery_info, status: f.status, featured: f.featured,
      badge: f.badge || null, seo_title: f.seo_title, seo_description: f.seo_description,
      licenses: {
        creator: { enabled: f.cr_enabled, price: Number(f.cr_price), description: f.cr_desc, pdf_url: f.cr_pdf, rights: tolines(f.cr_rights) },
        reseller: { enabled: f.rs_enabled, price: Number(f.rs_price), description: f.rs_desc, pdf_url: f.rs_pdf, rights: tolines(f.rs_rights) },
      },
      partner: { enabled: f.p_enabled, base_price: Number(f.p_base), min_price: Number(f.p_min), max_price: Number(f.p_max) },
      delivery: { pdf_url: f.d_pdf, instructions: f.d_instr },
    };
    try {
      if (isNew) await api.post("/admin/products", payload);
      else await api.put(`/admin/products/${product.id}`, payload);
      toast.success("Saved");
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const Group = ({ title, children }) => (
    <div className="p-5 bg-white/5 border border-white/10 rounded-2xl space-y-3">
      <p className="text-white font-semibold">{title}</p>{children}
    </div>
  );
  const Field = ({ label, k, type = "text", ta }) => (
    <div>
      <label className="text-xs text-gray-500 mb-1 block">{label}</label>
      {ta ? <textarea rows={4} className={inp} value={f[k]} onChange={(e) => set(k, e.target.value)} />
        : <input type={type} className={inp} value={f[k]} onChange={(e) => set(k, type === "checkbox" ? e.target.checked : e.target.value)} />}
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-white">{isNew ? "New Product" : "Edit Product"}</h1>
        <div className="flex gap-2">
          <Button onClick={onClose} variant="outline" className="border-white/20 text-white bg-transparent"><X className="w-4 h-4" /></Button>
          <Button onClick={save} className="bg-purple-600 text-white" data-testid="admin-save-product">Save</Button>
        </div>
      </div>
      <div className="space-y-5">
        <Group title="Basic Info">
          <Field label="Product Name" k="name" />
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Category</label>
            <select className={inp} value={f.category_id} onChange={(e) => set("category_id", e.target.value)}>
              <option value="" className="bg-[#0B0B0D]">Select</option>
              {cats.map((c) => <option key={c.id} value={c.id} className="bg-[#0B0B0D]">{c.name}</option>)}
            </select>
          </div>
          <div>
  <label className="text-xs text-gray-500 mb-1 block">Cover Image</label>
  <input
    type="file"
    accept="image/*"
    className={inp}
    onChange={(e) => {
      const file = e.target.files?.[0];
      if (file) {
        uploadCoverImage(file);
      }
    }}
  />
            {uploadingCover && (
  <p className="text-xs text-gray-400 mt-2">Uploading image...</p>
)}
            {f.cover_image && (
  <div className="mt-3 flex items-center gap-3">
    <img
      src={f.cover_image}
      alt="Cover preview"
      className="w-20 h-20 object-cover rounded"
    />

    <button
      type="button"
      onClick={() => set("cover_image", "")}
      className="text-xs text-red-400 hover:text-red-300"
    >
      Remove
    </button>
  </div>
)}
</div>
          <div>
  <label className="text-xs text-gray-500 mb-1 block">
    Gallery Images
  </label>
  <input
    type="file"
    accept="image/*"
    multiple
    className={inp}
    onChange={(e) => {
  const files = Array.from(e.target.files || []);
  uploadGalleryImages(files);
}}
  />
            {uploadingGallery && (
  <p className="text-xs text-gray-400 mt-2">
    Uploading gallery images...
  </p>
)}
            {f.gallery && (
  <div className="mt-3 space-y-2">
    {tolines(f.gallery).map((url, index) => (
      <div key={`${url}-${index}`} className="flex items-center gap-2">
        <img
          src={url}
          alt={`Gallery ${index + 1}`}
          className="w-16 h-16 object-cover rounded"
        />
        <button
          type="button"
          onClick={() => {
            const updated = tolines(f.gallery).filter((_, i) => i !== index);
            set("gallery", updated.join("\n"));
          }}
          className="text-xs text-red-400 hover:text-red-300"
        >
          Remove
        </button>
      </div>
    ))}
  </div>
)}
</div>
          
          <Field
  label="Sample Download URL"
  k="sample_url"
/>
          
          <Field label="Short Description" k="short_description" ta />
          <Field label="Full Description" k="full_description" ta />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Status</label>
              <select className={inp} value={f.status} onChange={(e) => set("status", e.target.value)}>
                {["draft", "published", "archived"].map((s) => <option key={s} value={s} className="bg-[#0B0B0D]">{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Badge (New/Popular)</label>
              <input className={inp} value={f.badge} onChange={(e) => set("badge", e.target.value)} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-400"><input type="checkbox" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured on homepage</label>
        </Group>

        <Group title="Content Sections (one item per line)">
          <Field label="Highlights" k="highlights" ta />
          <Field label="What's Included" k="whats_included" ta />
          <Field label="Who It's For" k="who_its_for" ta />
          <Field label="How It Works" k="how_it_works" ta />
        </Group>

        <Group title="Creator License">
          <label className="flex items-center gap-2 text-sm text-gray-400"><input type="checkbox" checked={f.cr_enabled} onChange={(e) => set("cr_enabled", e.target.checked)} /> Enabled</label>
          <Field label="Price (₹)" k="cr_price" type="number" />
          <Field label="Description" k="cr_desc" ta />
          <Field label="License PDF URL" k="cr_pdf" />
          <Field label="Rights (one per line)" k="cr_rights" ta />
        </Group>

        <Group title="Reseller License">
          <label className="flex items-center gap-2 text-sm text-gray-400"><input type="checkbox" checked={f.rs_enabled} onChange={(e) => set("rs_enabled", e.target.checked)} /> Enabled</label>
          <Field label="Price (₹)" k="rs_price" type="number" />
          <Field label="Description" k="rs_desc" ta />
          <Field label="License PDF URL" k="rs_pdf" />
          <Field label="Rights (one per line)" k="rs_rights" ta />
        </Group>

        <Group title="Partner Selling">
          <label className="flex items-center gap-2 text-sm text-gray-400"><input type="checkbox" checked={f.p_enabled} onChange={(e) => set("p_enabled", e.target.checked)} /> Enable partner selling</label>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Base Price" k="p_base" type="number" />
            <Field label="Min Price" k="p_min" type="number" />
            <Field label="Max Price" k="p_max" type="number" />
          </div>
        </Group>

        <Group title="Secure Delivery (never shown publicly)">
          <Field label="Delivery PDF/Resource URL" k="d_pdf" />
          <Field label="Delivery Instructions" k="d_instr" ta />
          <Field label="Public Delivery Info" k="delivery_info" ta />
        </Group>

        <Group title="SEO">
          <Field label="SEO Title" k="seo_title" />
          <Field label="SEO Description" k="seo_description" ta />
        </Group>
      </div>
    </div>
  );
};
